import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MdClose, MdDone, MdErrorOutline, MdFolderShared } from 'react-icons/md';
import api from '../api';
import { auth } from '../firebase';
import './ReleaseNotesModal.css';

const RELEASE_NOTES_SEEN_STORAGE_PREFIX = 'realtorch.releaseNotes.seen.';

function formatDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function normalizeSections(note) {
  const sections = note?.content?.sections;
  if (Array.isArray(sections) && sections.length) {
    return sections
      .map((section) => ({
        title: section?.title || 'Changes',
        items: Array.isArray(section?.items) ? section.items.filter(Boolean) : [],
      }))
      .filter((section) => section.items.length);
  }
  return [];
}

function getReleaseSortTime(note) {
  const value = note?.release_date || note?.published_at || note?.created_at;
  if (!value) return 0;
  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

function sortReleaseNotesDesc(notes) {
  return [...notes].sort((a, b) => {
    const timeDiff = getReleaseSortTime(b) - getReleaseSortTime(a);
    if (timeDiff !== 0) return timeDiff;
    return Number(b?.id || 0) - Number(a?.id || 0);
  });
}

function getSeenStorageKey(userId) {
  return `${RELEASE_NOTES_SEEN_STORAGE_PREFIX}${userId}`;
}

function readLocalSeenRevisions(userId) {
  if (!userId || typeof window === 'undefined') return {};
  try {
    const parsed = JSON.parse(window.localStorage.getItem(getSeenStorageKey(userId)) || '{}');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function writeLocalSeenRevisions(userId, revisions) {
  if (!userId || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(getSeenStorageKey(userId), JSON.stringify(revisions));
  } catch {
    // Local acknowledgement is only a fallback; storage failures should never block the UI.
  }
}

function getNoteRevision(note) {
  return Number(note?.revision || 1);
}

function isNoteSeen(note, localSeenRevisions) {
  const revision = getNoteRevision(note);
  const serverSeenRevision = Number(note?.seen_revision || 0);
  const localSeenRevision = Number(localSeenRevisions?.[String(note?.id)] || 0);
  return serverSeenRevision >= revision || localSeenRevision >= revision;
}

function getLocallySeenServerPendingNotes(notes, localSeenRevisions) {
  if (!notes.length) return [];
  if (!Object.keys(localSeenRevisions || {}).length) return [];
  return notes.filter((note) => {
    const revision = getNoteRevision(note);
    const serverSeenRevision = Number(note?.seen_revision || 0);
    const localSeenRevision = Number(localSeenRevisions?.[String(note?.id)] || 0);
    return localSeenRevision >= revision && serverSeenRevision < revision;
  });
}

async function syncLocalAcknowledgements(notes, localSeenRevisions, onSynced) {
  const locallySeenServerPendingNotes = notes.filter((note) => {
    const revision = getNoteRevision(note);
    const serverSeenRevision = Number(note?.seen_revision || 0);
    const localSeenRevision = Number(localSeenRevisions?.[String(note?.id)] || 0);
    return localSeenRevision >= revision && serverSeenRevision < revision;
  });
  if (!locallySeenServerPendingNotes.length || !auth.currentUser) return false;

  try {
    await Promise.all(
      locallySeenServerPendingNotes.map((note) => api.post(`/api/release-notes/${note.id}/seen`))
    );
    if (onSynced) {
      onSynced(locallySeenServerPendingNotes);
    }
    return true;
  } catch (err) {
    console.warn('Release notes local acknowledgements could not be synced yet.', err);
    return false;
  }
}

export default function ReleaseNotesModal({ user, openKey = 0, onOpenCompanyWorkspaceGuide }) {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState('pending');
  const [notes, setNotes] = useState([]);
  const [localSeenRevisions, setLocalSeenRevisions] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const pendingNotes = useMemo(
    () => sortReleaseNotesDesc(
      notes.filter((note) => !isNoteSeen(note, localSeenRevisions))
    ),
    [localSeenRevisions, notes]
  );

  const loadNotes = useCallback(async ({ pendingOnly }) => {
    setLoading(true);
    setError('');
    try {
      const endpoint = pendingOnly ? '/api/release-notes/pending' : '/api/release-notes';
      const res = await api.get(endpoint, { params: { limit: pendingOnly ? 10 : 50 } });
      const nextNotes = Array.isArray(res.data?.release_notes) ? res.data.release_notes : [];
      setNotes(nextNotes);
      return nextNotes;
    } catch (err) {
      console.error('Failed to load release notes:', err);
      setError('Release notes could not be loaded.');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const markNotesServerSeen = useCallback((syncedNotes) => {
    if (!syncedNotes?.length) return;
    const seenAt = new Date().toISOString();
    const revisionById = syncedNotes.reduce((acc, note) => {
      acc[String(note.id)] = getNoteRevision(note);
      return acc;
    }, {});
    setNotes((prev) => prev.map((note) => {
      const syncedRevision = revisionById[String(note.id)];
      return syncedRevision
        ? { ...note, seen_revision: syncedRevision, seen_at: seenAt }
        : note;
    }));
  }, []);

  const syncLocalAcknowledgementsWithRetry = useCallback((sourceNotes, sourceLocalSeenRevisions) => {
    const pendingSyncNotes = getLocallySeenServerPendingNotes(sourceNotes, sourceLocalSeenRevisions);
    if (!pendingSyncNotes.length) return;

    const attemptSync = () => {
      syncLocalAcknowledgements(sourceNotes, sourceLocalSeenRevisions, markNotesServerSeen).then((synced) => {
        if (!synced) {
          window.setTimeout(() => {
            syncLocalAcknowledgements(sourceNotes, sourceLocalSeenRevisions, markNotesServerSeen);
          }, 3000);
        }
      });
    };

    if (typeof window === 'undefined') {
      attemptSync();
      return;
    }
    window.setTimeout(attemptSync, 250);
  }, [markNotesServerSeen]);

  useEffect(() => {
    setLocalSeenRevisions(readLocalSeenRevisions(user?.id));
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    loadNotes({ pendingOnly: true }).then((nextNotes) => {
      const nextLocalSeenRevisions = readLocalSeenRevisions(user.id);
      const nextPendingNotes = nextNotes.filter((note) => !isNoteSeen(note, nextLocalSeenRevisions));
      if (!cancelled) {
        setLocalSeenRevisions(nextLocalSeenRevisions);
      }
      syncLocalAcknowledgementsWithRetry(nextNotes, nextLocalSeenRevisions);
      if (!cancelled && nextPendingNotes.length > 0) {
        setMode('pending');
        setIsOpen(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [user?.id, loadNotes, syncLocalAcknowledgementsWithRetry]);

  useEffect(() => {
    if (!user?.id || !openKey) return;
    let cancelled = false;
    loadNotes({ pendingOnly: false }).then(() => {
      if (!cancelled) {
        setMode('timeline');
        setIsOpen(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [user?.id, openKey, loadNotes]);

  const markSeen = useCallback(async (noteIds) => {
    const ids = noteIds.filter(Boolean).map((id) => String(id));
    setIsOpen(false);
    if (!ids.length) {
      return;
    }

    const idSet = new Set(ids);
    const revisionPatch = notes.reduce((acc, note) => {
      if (idSet.has(String(note.id))) {
        acc[String(note.id)] = getNoteRevision(note);
      }
      return acc;
    }, {});
    if (Object.keys(revisionPatch).length) {
      setLocalSeenRevisions((prev) => {
        const next = { ...prev, ...revisionPatch };
        writeLocalSeenRevisions(user?.id, next);
        return next;
      });
    }

    setError('');
    const nextLocalSeenRevisions = { ...localSeenRevisions, ...revisionPatch };
    syncLocalAcknowledgementsWithRetry(notes, nextLocalSeenRevisions);
  }, [localSeenRevisions, notes, syncLocalAcknowledgementsWithRetry, user?.id]);

  const acknowledgeAndClose = useCallback(() => {
    markSeen(pendingNotes.map((note) => note.id));
  }, [markSeen, pendingNotes]);

  if (!isOpen) return null;

  const title = mode === 'pending' ? "What's new" : 'Release notes';
  const visibleNotes = mode === 'pending' ? pendingNotes : sortReleaseNotesDesc(notes);

  return (
    <div className="release-notes-overlay" role="presentation">
      <div className="release-notes-modal" role="dialog" aria-modal="true" aria-labelledby="release-notes-title">
        <div className="release-notes-header">
          <div className="release-notes-title-wrap">
            <span className="release-notes-icon"><MdErrorOutline size={18} /></span>
            <div>
              <h2 id="release-notes-title">{title}</h2>
              <p>{mode === 'pending' ? 'Updates since you last checked in.' : 'A timeline of recent RealTorch updates.'}</p>
            </div>
          </div>
          <button
            type="button"
            className="release-notes-close"
            onClick={acknowledgeAndClose}
            aria-label="Close release notes"
          >
            <MdClose size={20} />
          </button>
        </div>

        <div className="release-notes-body">
          {loading && <div className="release-notes-state">Loading release notes...</div>}
          {error && <div className="release-notes-error">{error}</div>}
          {!loading && mode === 'timeline' && onOpenCompanyWorkspaceGuide && (
            <div className="release-notes-guide-card">
              <div className="release-notes-guide-icon">
                <MdFolderShared size={20} />
              </div>
              <div>
                <h3>Company Workspace Guide</h3>
                <p>Review how personal and company workspaces handle chats, files, memories, and company work history.</p>
              </div>
              <button
                type="button"
                className="release-notes-guide-button"
                onClick={onOpenCompanyWorkspaceGuide}
              >
                Open guide
              </button>
            </div>
          )}
          {!loading && !visibleNotes.length && (
            <div className="release-notes-state">No release notes yet.</div>
          )}

          {!loading && visibleNotes.map((note) => {
            const sections = normalizeSections(note);
            const unread = !isNoteSeen(note, localSeenRevisions);
            return (
              <article key={note.id} className="release-note-card">
                <div className="release-note-card-header">
                  <div>
                    <div className="release-note-date">{formatDate(note.release_date || note.published_at)}</div>
                    <h3>{note.title}</h3>
                  </div>
                  {unread && <span className="release-note-badge">New</span>}
                </div>
                {note.summary && <p className="release-note-summary">{note.summary}</p>}
                {sections.map((section, index) => (
                  <section key={`${note.id}-${section.title}-${index}`} className="release-note-section">
                    <h4>{section.title}</h4>
                    <ul>
                      {section.items.map((item, itemIndex) => (
                        <li key={`${note.id}-${index}-${itemIndex}`}>{item}</li>
                      ))}
                    </ul>
                  </section>
                ))}
              </article>
            );
          })}
        </div>

        <div className="release-notes-footer">
          <p className="release-notes-footer-hint">
            Click the bottom-left profile icon to see previous release notes.
          </p>
          <button
            type="button"
            className="release-notes-primary"
            onClick={acknowledgeAndClose}
          >
            <MdDone size={18} />
            <span>Got it</span>
          </button>
        </div>
      </div>
    </div>
  );
}
